import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { Cable, Lightbulb, Zap, Search, X, Rocket, Download, Plus } from 'lucide-react';
import * as XLSX from 'xlsx';
import { ProjectKeyPanel } from './ProjectKeyPanel';
import { WireRunsPanel } from './WireRunsPanel';
import { supabase } from '../lib/supabase';
import { fetchAnnotationCountMapsV2 } from '../lib/annotationV2Persistence';
import '../styles/AgentChat.css';

interface CountingPanelProps {
  estimation: any;
  pdfAnnotatorRef?: React.RefObject<any>;
  onAnnotationCountsChange?: (counts: Map<string, Map<number, number>>) => void;
  filteredComponent?: string | null;
  onFilteredComponentChange?: (componentName: string | null) => void;
  selectedForPlacement?: string | null;
  onComponentSelectForPlacement?: (component: { name: string; symbol: string; color: string; symbolType?: string } | null) => void;
  refreshTrigger?: number;
  wireRunRefreshTrigger?: number;
}

const CountingPanel = forwardRef<any, CountingPanelProps>(({
  estimation,
  pdfAnnotatorRef,
  onAnnotationCountsChange,
  filteredComponent,
  onFilteredComponentChange,
  selectedForPlacement,
  onComponentSelectForPlacement,
  refreshTrigger,
  wireRunRefreshTrigger,
}, ref) => {
  const [unifiedSearchTerm, setUnifiedSearchTerm] = useState('');
  const [powerMatchCount, setPowerMatchCount] = useState(0);
  const [lightingMatchCount, setLightingMatchCount] = useState(0);
  const [countAllPowerAction, setCountAllPowerAction] = useState<(() => void) | null>(null);
  const [countAllLightingAction, setCountAllLightingAction] = useState<(() => void) | null>(null);
  const [openAddPowerAction, setOpenAddPowerAction] = useState<(() => void) | null>(null);
  const [openAddLightingAction, setOpenAddLightingAction] = useState<(() => void) | null>(null);
  const [isCountAllPowerDisabled, setIsCountAllPowerDisabled] = useState(true);
  const [isCountAllLightingDisabled, setIsCountAllLightingDisabled] = useState(true);
  const [isLightingExtractMode, setIsLightingExtractMode] = useState(false);
  const [showAddComponentTypeModal, setShowAddComponentTypeModal] = useState(false);
  const powerSectionRef = useRef<HTMLDivElement | null>(null);
  const wiresSectionRef = useRef<HTMLDivElement | null>(null);

  const exportTakeoffXlsx = useCallback(async () => {
    const estimationId = estimation?.id;
    if (!estimationId) return;

    const [keyResult, wireResult] = await Promise.all([
      supabase.from('estimations').select('project_key').eq('id', estimationId).single(),
      supabase.from('wire_runs').select('*').eq('estimation_id', estimationId).order('created_at', { ascending: false }),
    ]);

    const rawComponents = keyResult.data?.project_key?.components || [];
    const annotationCounts = await fetchAnnotationCountMapsV2({
      supabase: supabase as any,
      estimationId,
    });

    const isSwitchByName = (name: string) => {
      const n = (name || '').toLowerCase();
      return (n.includes('switch') || n.includes('dimmer')) && !n.includes('receptacle');
    };

    const isLightingComponent = (c: any) => c.symbolType === 'Lighting' || c.componentType === 'Lighting';
    const isPowerComponent = (c: any) => !isLightingComponent(c);

    // Build component map from project key
    const componentMap = new Map<string, any>();
    rawComponents.forEach((c: any) => {
      const key = isLightingComponent(c) ? (c.symbol || c.name) : c.name;
      componentMap.set(key, c);
    });

    // Also include any component types from annotation counts that aren't in the project key
    annotationCounts.forEach((_pageCounts, key) => {
      if (!componentMap.has(key)) {
        componentMap.set(key, {
          name: key,
          symbol: '',
          symbolType: isSwitchByName(key) ? 'Power' : undefined,
          componentType: isSwitchByName(key) ? 'Switches' : undefined,
        });
      }
    });

    const allComponents = Array.from(componentMap.values());

    const getTotal = (key: string) => {
      const counts = annotationCounts.get(key);
      let total = 0;
      if (counts) counts.forEach((c: number) => { total += c; });
      return total;
    };

    // Power sheet
    const powerRows = allComponents
      .filter(isPowerComponent)
      .map((c: any) => ({
        Component: c.name,
        Count: getTotal(c.name),
      }))
      .filter(r => r.Count > 0 || rawComponents.some((rc: any) => rc.name === r.Component));

    // Lighting sheet
    const lightingRows = allComponents
      .filter(isLightingComponent)
      .map((c: any) => ({
        'Fixture Code': (c.symbol || '').trim(),
        Component: c.name,
        Manufacturer: c.manufacturer || '',
        Count: getTotal(c.symbol || c.name),
      }))
      .filter(r => r.Count > 0 || rawComponents.some((rc: any) => rc.name === r.Component));

    // Wires sheet
    const wireRuns = wireResult.data || [];
    const wireRows = wireRuns.map((run: any) => ({
      'Wire Type': run.wire_type?.name || '',
      Category: run.wire_type?.category || '',
      Gauge: run.wire_type?.gauge || '',
      Page: run.page_number,
      'Length (ft)': Math.round(run.total_length * 100) / 100,
    }));

    const wb = XLSX.utils.book_new();
    const powerWs = XLSX.utils.json_to_sheet(powerRows.length ? powerRows : [{ Component: 'No power devices found' }]);
    const lightingWs = XLSX.utils.json_to_sheet(
      lightingRows.length
        ? lightingRows
        : [{ 'Fixture Code': '', Component: 'No lighting fixtures found', Manufacturer: '', Count: '' }]
    );
    const wireWs = XLSX.utils.json_to_sheet(wireRows.length ? wireRows : [{ 'Wire Type': 'No wire runs found' }]);

    XLSX.utils.book_append_sheet(wb, powerWs, 'Power Devices');
    XLSX.utils.book_append_sheet(wb, lightingWs, 'Lighting Fixtures');
    XLSX.utils.book_append_sheet(wb, wireWs, 'Wire Runs');

    XLSX.writeFile(wb, `takeoff-${estimationId}.xlsx`);
  }, [estimation]);

  const handlePowerMatchCount = useCallback((count: number) => setPowerMatchCount(count), []);
  const handleLightingMatchCount = useCallback((count: number) => setLightingMatchCount(count), []);
  const handleRegisterCountAllPower = useCallback((action: (() => void) | null, disabled: boolean) => {
    setCountAllPowerAction(() => action);
    setIsCountAllPowerDisabled(disabled);
  }, []);
  const handleRegisterCountAllLighting = useCallback((action: (() => void) | null, disabled: boolean, isExtractMode?: boolean) => {
    setCountAllLightingAction(() => action);
    setIsCountAllLightingDisabled(disabled);
    setIsLightingExtractMode(isExtractMode ?? false);
  }, []);
  const handleRegisterAddPowerAction = useCallback((action: (() => void) | null) => {
    setOpenAddPowerAction(() => action);
  }, []);
  const handleRegisterAddLightingAction = useCallback((action: (() => void) | null) => {
    setOpenAddLightingAction(() => action);
  }, []);

  useImperativeHandle(ref, () => ({
    switchToWiresTab: () => {
      wiresSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    switchToCountingTab: () => {
      powerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }), []);

  const handleOpenAddPower = useCallback(() => {
    setShowAddComponentTypeModal(false);
    if (openAddPowerAction) {
      openAddPowerAction();
      return;
    }
    pdfAnnotatorRef?.current?.openAddNewComponentModal?.();
  }, [openAddPowerAction, pdfAnnotatorRef]);

  const handleOpenAddLighting = useCallback(() => {
    setShowAddComponentTypeModal(false);
    if (openAddLightingAction) {
      openAddLightingAction();
      return;
    }
    pdfAnnotatorRef?.current?.openAddNewComponentModal?.();
  }, [openAddLightingAction, pdfAnnotatorRef]);

  const hasWireCalibration = Boolean(pdfAnnotatorRef?.current?.hasCalibration);
  const isInAddMode = Boolean(selectedForPlacement);

  return (
    <div className="counting-tab-content">
      <div className="counting-panel-header">
        <div className="counting-panel-header-left">
          <Rocket size={22} />
          <span className="counting-panel-title">Takeoff</span>
        </div>
        <button onClick={exportTakeoffXlsx} className="counting-panel-csv-btn" title="Download Takeoff as CSV">
          <Download size={16} />
          <span>Download Takeoff</span>
        </button>
      </div>
      {/* <div className="unified-search-bar">
        <Search size={16} className="unified-search-icon" />
        <input
          type="text"
          placeholder="Search all components..."
          value={unifiedSearchTerm}
          onChange={(e) => setUnifiedSearchTerm(e.target.value)}
          className="unified-search-input"
        />
        {unifiedSearchTerm && (
          <button
            onClick={() => setUnifiedSearchTerm('')}
            className="unified-search-clear"
            title="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div> */}

      <div className="count-all-actions-row">
        <div
          style={{
            width: '100%',
            minHeight: '48px',
            padding: '12px',
            background: 'var(--bg-primary)',
            color: 'var(--text-secondary)',
            fontWeight: 500,
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            boxSizing: 'border-box',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <span>Click the</span>
            <span
              aria-hidden
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                borderRadius: '999px',
                color: '#005bff',
                boxShadow: '0 3px 8px rgba(59, 130, 246, 0.2), 0 0 0 1px rgba(59, 130, 246, 0.18) inset',
                background: 'transparent',
              }}
            >
              <Plus size={10} strokeWidth={2.8} />
            </span>
            <span>
              {isInAddMode
                ? 'button again in order to get out of add mode OR press ESCAPE.'
                : 'button in any row to add the component to the drawing.'}
            </span>
          </span>
        </div>
      </div>

      {showAddComponentTypeModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002,
          }}
          onClick={() => setShowAddComponentTypeModal(false)}
        >
          <div
            style={{
              width: 'min(92vw, 430px)',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: 12,
              padding: 18,
              boxShadow: '0 18px 40px rgba(0, 0, 0, 0.28)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, marginBottom: 8, color: 'var(--text-primary)', fontSize: 18 }}>
              Add Component Type
            </h3>
            <p style={{ margin: 0, marginBottom: 14, color: 'var(--text-secondary)', fontSize: 13 }}>
              Choose whether you want to add a power component or a lighting fixture.
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              <button
                className="count-all-power-btn"
                onClick={handleOpenAddPower}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <Zap size={14} />
                <span>Add Power Component</span>
              </button>
              <button
                className="count-all-lighting-btn"
                onClick={handleOpenAddLighting}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                <Lightbulb size={14} />
                <span>Add Lighting Fixture</span>
              </button>
              <button
                onClick={() => setShowAddComponentTypeModal(false)}
                style={{
                  border: '1px solid var(--border-strong)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Power Section */}
      <div ref={powerSectionRef}>
        <div className={`accordion-header power-tab active ${unifiedSearchTerm && powerMatchCount > 0 ? 'has-matches' : ''} ${unifiedSearchTerm && powerMatchCount === 0 ? 'no-matches' : ''}`}>
          <Zap size={14} />
          <span>Power Devices</span>
          {unifiedSearchTerm && <span className="accordion-match-count">{powerMatchCount}</span>}
          <button
            type="button"
            className="counting-header-add-pill"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAddPower();
            }}
            title="Add Power Component"
            aria-label="Add Power Component"
          >
            <Plus size={13} strokeWidth={2.6} />
            <span>Add Power Component</span>
          </button>
        </div>
        <div className="accordion-panel">
          <ProjectKeyPanel
            estimationId={estimation?.id}
            pdfAnnotatorRef={pdfAnnotatorRef}
            onAnnotationCountsChange={onAnnotationCountsChange}
            filteredComponent={filteredComponent}
            onFilteredComponentChange={onFilteredComponentChange}
            hideExtraction={true}
            selectedForPlacement={selectedForPlacement}
            onComponentSelectForPlacement={onComponentSelectForPlacement}
            refreshTrigger={refreshTrigger}
            externalSearchTerm={unifiedSearchTerm}
            onMatchCount={handlePowerMatchCount}
            hidePrimaryCountButtons={true}
            hideAddComponentButton={true}
            onRegisterCountAllPower={handleRegisterCountAllPower}
            onRegisterAddComponentAction={handleRegisterAddPowerAction}
          />
        </div>
      </div>

      {/* Lighting Section */}
      <div>
        <div className={`accordion-header lighting-tab active ${unifiedSearchTerm && lightingMatchCount > 0 ? 'has-matches' : ''} ${unifiedSearchTerm && lightingMatchCount === 0 ? 'no-matches' : ''}`}>
          <Lightbulb size={14} />
          <span>Lighting Fixtures</span>
          {unifiedSearchTerm && <span className="accordion-match-count">{lightingMatchCount}</span>}
          <button
            type="button"
            className="counting-header-add-pill"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAddLighting();
            }}
            title="Add Lighting Fixture"
            aria-label="Add Lighting Fixture"
          >
            <Plus size={13} strokeWidth={2.6} />
            <span>Add Lighting Fixture</span>
          </button>
        </div>
        <div className="accordion-panel">
          <ProjectKeyPanel
            estimationId={estimation?.id}
            pdfAnnotatorRef={pdfAnnotatorRef}
            onAnnotationCountsChange={onAnnotationCountsChange}
            filteredComponent={filteredComponent}
            onFilteredComponentChange={onFilteredComponentChange}
            hideExtraction={true}
            selectedForPlacement={selectedForPlacement}
            onComponentSelectForPlacement={onComponentSelectForPlacement}
            filterSymbolType="Lighting"
            refreshTrigger={refreshTrigger}
            externalSearchTerm={unifiedSearchTerm}
            onMatchCount={handleLightingMatchCount}
            hidePrimaryCountButtons={true}
            hideAddComponentButton={true}
            onRegisterCountAllLighting={handleRegisterCountAllLighting}
            onRegisterAddComponentAction={handleRegisterAddLightingAction}
          />
        </div>
      </div>

      {/* Wires Section */}
      {/* <div ref={wiresSectionRef}>
        <div className="accordion-header wires-tab active">
          <Cable size={14} />
          <span>Wires</span>
          <button
            className="new-run-btn wire-section-new-run-btn"
            onClick={() => {
              pdfAnnotatorRef?.current?.startWireRunMode?.();
            }}
            disabled={!hasWireCalibration}
            title={hasWireCalibration ? 'Start new wire run' : 'Calibrate scale first'}
          >
            <Plus size={12} />
            New Run
          </button>
        </div>
        <div className="accordion-panel">
          <WireRunsPanel
            estimationId={estimation?.id || ''}
            fileId={pdfAnnotatorRef?.current?.currentDrawingFileId}
            hasCalibration={pdfAnnotatorRef?.current?.hasCalibration || false}
            hidePanelTitle={true}
            hideNewRunButton={true}
            onStartWireRun={() => {
              pdfAnnotatorRef?.current?.startWireRunMode?.();
            }}
            onStartWireRunForType={(wireType) => {
              pdfAnnotatorRef?.current?.startWireRunWithType?.(wireType);
            }}
            onOpenCalibration={() => {
              const annotator = pdfAnnotatorRef?.current;
              if (annotator?.startScaleCalibration) {
                annotator.startScaleCalibration();
                return;
              }
              annotator?.openCalibrationDropdown?.();
            }}
            onSelectRun={(runId: string, pageNumber: number) => {
              pdfAnnotatorRef?.current?.selectWireRunAnnotation?.(runId, pageNumber);
            }}
            onWireRunDeleted={(runId: string, pageNumber: number) => {
              pdfAnnotatorRef?.current?.deleteWireRunAnnotation?.(runId, pageNumber);
            }}
            refreshTrigger={wireRunRefreshTrigger}
          />
        </div>
      </div> */}
    </div>
  );
});

CountingPanel.displayName = 'CountingPanel';

export default CountingPanel;
